// Ported from dev-scrips-pm-react src/patients/services/use-patient-documents.ts (PROD-1119 Patients data layer).
import { useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type {
  PatientDocument,
  PatientDocumentsRequest,
} from '@/types/patient-chart-api';

/**
 * Patient documents list — Chart > Documents tab (PROD-738 PR B).
 *
 * Wire (Flutter `document_data_source_impl.dart:25`):
 *
 *   POST /api/Patients/GetPatientDocuments
 *        body: PatientDocumentsRequest
 *        → PatientDocument[]
 *
 * The endpoint is paginated server-side (`pageNumber` + `pageSize`).
 * Default `pageSize` is 20 — matches Flutter's
 * `defaultPatientDocumentPageSize`.
 *
 * Filter set (all optional): date range (`startDate`/`endDate`),
 * `fileType[]`, `uploadedBy[]`, `isArchived`. PR B keeps the request
 * minimal (patientId + pagination); filter UI ships in PR B.next if
 * needed.
 */

const DEFAULT_PAGE_SIZE = 20;

export interface UsePatientDocumentsParams {
  patientId: string | null | undefined;
  /**
   * PROD-1238: scope the list to one encounter (the encounter Docs step —
   * Flutter's `FetchPatientEncounterDocument` always sends the encounter id,
   * `patient_encounter_bloc.dart:2452`). Omitted/null → the patient-wide list
   * (the chart tab; backend returns all documents, dev-curl verified).
   */
  encounterId?: string | null;
  pageNumber?: number;
  pageSize?: number;
  /**
   * PROD-900: toggle between Active (default) and Archived lists. The
   * backend filters server-side via `body.isArchived` — when `true`,
   * the response includes ONLY documents with `isDeleted: true`. The
   * chart-tab Documents view drives this from the Status segmented
   * control alongside the section header.
   */
  isArchived?: boolean;
  /** ISO date string `YYYY-MM-DD`; backend filters `documentDate >=`. */
  startDate?: string | null;
  /** ISO date string `YYYY-MM-DD`; backend filters `documentDate <=`. */
  endDate?: string | null;
  /** Selected file-type wire literals (`Notes` / `Labs` / etc.). */
  fileType?: string[];
  /** Selected uploader user-ids. */
  uploadedBy?: string[];
}

export function usePatientDocuments(params: UsePatientDocumentsParams) {
  const isArchived = params.isArchived ?? false;
  const startDate = params.startDate ?? null;
  const endDate = params.endDate ?? null;
  const fileType = params.fileType ?? [];
  const uploadedBy = params.uploadedBy ?? [];
  const body: PatientDocumentsRequest = {
    patientId: params.patientId ?? null,
    encounterId: params.encounterId ?? null,
    pageNumber: params.pageNumber ?? 1,
    pageSize: params.pageSize ?? DEFAULT_PAGE_SIZE,
    isArchived,
    startDate,
    endDate,
    fileType: fileType as never,
    uploadedBy,
  };

  return useQuery({
    queryKey: [
      'patient-documents',
      params.patientId,
      params.encounterId ?? null,
      body.pageNumber,
      body.pageSize,
      isArchived,
      startDate,
      endDate,
      fileType,
      uploadedBy,
    ],
    queryFn: ({ signal }) =>
      post<PatientDocument[]>(
        '/api/Patients/GetPatientDocuments',
        body,
        { signal },
      ),
    enabled: !!params.patientId,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}
